import $ from 'jquery';

import { Injectable } from '@angular/core';

import { ErrorModel }              from './typings/error';
import { FormGroup }               from '@angular/forms';
import { MessageModel }            from './typings/message';
import { MessageOptionsModel }     from './typings/message.options';
import { MessageTypeModel }        from './typings/message.type';
import { ModelControllerAjsModel } from '../shared/types/model-controller.ajs';
import { ProperCasePipe }          from '../shared/pipes/proper-case.pipe';
import { SessionService }          from '../sessions/session.service';

@Injectable({
  providedIn: 'root'
})
export class MessagesService {
  private _defaultOptions : MessageOptionsModel = {
    autohide: false,
    delay   : 5000
  };

  public messages : Array<MessageModel> = [];

  constructor (
    private properCasePipe: ProperCasePipe,
    private sessionService: SessionService
  ) {}

  _getDefaultForm () : ng.IFormController {
    const formElementRef : JQuery = $('form');

    return formElementRef.length
      ? ( formElementRef.scope() as any ).form
      : null;
  }

  addMessage ( message : MessageModel ) : void {
    this.messages.push(message);
  }

  clearErrors () : void {
    this.messages = this.messages.filter(message => message.type !== 'error');
  }

  clientError ( form : ng.IFormController ) : void {
    let isFirstLoop = true;

    const message : MessageModel = {
      header : '',
      options: this.getOptions('error'),
      type   : 'error'
    };

    /**
     * If this is used from an Angular form
     * then we can't loop through the controls
     * in the same way. Right now, that piece
     * is handled in the form submitter.
     */
    if ( form instanceof FormGroup ) {
      message.header = 'Please fix errors';

      this.messages.push(message);

      return;
    }

    for ( const type in form.$error ) {
      if ( Array.isArray(form.$error[ type ]) ) {
        form.$error[ type ].forEach(error => {
          if ( form[ error.$name ].$setViewValue ) {
            form[ error.$name ].$setViewValue(form[ error.$name ].$viewValue);
            form[ error.$name ].$setDirty();
          }
          else {
            this.clientError(form[ error.$name ]);

            return;
          }

          if ( isFirstLoop ) {
            $(`#${ error.$name }`).trigger('focus');

            message.header = 'Please fix errors';

            this.messages.push(message);
          }

          isFirstLoop = false;
        });
      }
    }
  }

  error ( response : any, form : ( ng.IFormController ) = this._getDefaultForm() ) : void {
    /**
     * When a modal is dismissed in Angular, it throws an error. To prevent
     * the error from propagating down to the "Unknown Error" piece, we look
     * for an empty response and ignore it.
     *
     * REFERENCE: WS-5328
     */
    if ( !response ) {
      return;
    }

    const error = this.formatError(response);

    const message : MessageModel = {
      header : '',
      options: this.getOptions('error'),
      type   : 'error'
    };

    if ( typeof response === 'string' ) {
      message.header = error.message;

      this.messages.push(message);
    }

    else if ( error.message ) {

      /**
       * Field Error
       *
       * Use the form to highlight the
       * fields that are invalid.
       */
      if ( this.isFieldError(error) ) {
        let isFirstLoop = true;

        for ( const key in error.field_errors ) {

          /**
           * if the control exists on the passed in form,
           * set the validity and state here.
           */
          if ( form[ key ]) {
            const control : ModelControllerAjsModel = form[ key ];

            control.serverError   = true;
            control.serverMessage = this.properCasePipe.transform(error.field_errors[ key ][ 0 ], { sentence: true });

            control.$setDirty();
            control.$setValidity('server', false);

            /**
             * Usually the control will have the same ID
             * as control name. So, focus the first element
             * here, if possible.
             */
            const controlElementRef : JQuery = $(`#${ key }`);

            if ( controlElementRef.length ) {
              if ( isFirstLoop ) {
                controlElementRef.focus();
              }

              controlElementRef.on('keypress', () => {
                control.serverError   = false;
                control.serverMessage = null;

                control.$setValidity('server', true);

                controlElementRef.off('keypress');
              });
            }

            isFirstLoop = false;

          }

          /**
            * If a control can't be found, then build
            * an error message with all the various
            * field errors and display it.
           */
          else {
            let text = error.message;

            for ( const property in error.field_errors ) {
              for ( let i = 0; i < error.field_errors[ property ].length; i++ ) {
                if ( property === 'base' ) {
                  text = error.message.concat(`<br>${ error.field_errors[ property ][ i ] }<br>`);
                }
                else {
                  text = error.message.concat(`<br>${ property }: ${ error.field_errors[ property ][ i ] } <br>`);
                }
              }
            }

            message.header = text;

            this.messages.push(message);
          }
        }
      }

      /**
       * Session Error
       *
       * Logout the user and redirect
       * to the login page. Save the error in session
       * storage so that it can be displayed on the
       * login page.
       */
      else if ( this.isSessionError(error) ) {
        window.location.assign('login.html');

        this.sessionService.clear();

        this.sessionService.setKey('error_msg', error.message);
      }

      /**
       * Unauthorized Error
       *
       * Display error but ensure only one is show in case
       * there are multiple calls on the same page.
       */
      else if ( this.isUnauthorizedError(error) ) {
        console.error(error.message);

        if ( !this.messages.length ) {
          message.header = this.properCasePipe.transform(error.message, { sentence: true });

          this.messages.push(message);
        }
      }

      /**
       * Generic Global Error
       *
       * Clear other messages and display this
       * one only.
       */
      else if ( this.isGlobalError(error) ) {
        this.messages = [];

        console.error(error.message);

        message.header = this.properCasePipe.transform(error.message, { sentence: true });

        this.messages.push(message);
      }

      /**
       * Unknown Error
       *
       * Server error or ruby error that is not
       * formatted in a readable way. So, display
       * a generic message and show the server error
       * in the console.
       */
      else {
        console.error(error.message);

        if ( !this.messages.length ) {
          message.header = 'Unknown error, please contact support (support@workshopsoftware.com) for further details.';

          this.messages.push(message);
        }
      }

    }

    /**
     * No Message Returned
     *
     * Show a generic server error.
     */
    else {
      console.error(error);

      message.header = 'Error, could not reach server. Try again later';

      this.messages.push(message);
    }
  }

  formatError ( error : any = {} ) : ErrorModel {
    if ( typeof error === 'string' ) {
      return {
        message: error
      };
    }

    if ( error.data ) {
      return error.data;
    }

    return error;
  }

  getOptions ( type : MessageTypeModel = 'success', options : MessageOptionsModel = {} ) : MessageOptionsModel {
    switch (type) {
      case 'error':
        options.autohide = false;
        break;
      case 'success':
      case 'warning':
        options.autohide = true;
        options.delay    = this._defaultOptions.delay;
        break;
      default:
        options = this._defaultOptions;
        break;
    }

    return options;
  }

  isFieldError ( error : any = {} ) : boolean {
    return error.type === 'F';
  }

  isGlobalError ( error : any = {} ) : boolean {
    return error.type === 'G';
  }

  isSessionError ( error : any = {} ) : boolean {
    return error.type === 'X';
  }

  isUnauthorizedError ( error : any = {} ) : boolean {
    return error.type === 'I';
  }

  remove ( message : MessageModel ) : void {
    this.messages = this.messages.filter(item => item !== message);
  }

  show ( message : string, type : MessageTypeModel = 'success', options ?: MessageOptionsModel ) : void {
    this.messages.push({
      header : this.properCasePipe.transform(message, { sentence: true }),
      options: this.getOptions(type, options),
      type
    });
  }
}
